import { useParams } from "react-router-dom";
import { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, Input, Select } from "@jhool-io/fe-components";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
    CPT_CODES_DESCRIPTION,
    CPT_CODES_PSYCH,
} from "../../../../utils/constants";
import {
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import {
    AppointmentLocation,
    SessionPlaceOfServiceCode,
} from "../../../../utils/types/session";
import {
    IChangeCodeResponse,
    INote,
    ModifierTypes,
    NoteLabels,
    NoteTypes,
} from "../../types/notes.types";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import { useChangeCptCode } from "../../../../hooks/mutations/note";
import useToast from "../../../../hooks/useToast";
import { useDebounce } from "../../../../hooks/helpers";
import { IPaginatedApiResponse } from "../../../../utils/types/api-response";
import Loader from "../../../../shared-ui/Loader/Loader";

interface ChangeCodeFormPsychProps {
    // Array of notes for bulk change code flow
    notes?: INote[];
    // Single note when changing code from note list
    note?: INote;
    // Note cpt code when running operation from note list
    cptCode?: string;
    // Function to call when form submit button is clicked
    onFormSubmit(): void;
    // Should the note details be visible or not
    showDetails?: boolean;
}

interface IChangeCodePsychPayload {
    cpt_code: string;
    pos_code?: string;
    modifier_codes?: string[];
    duration: string;
    total_units: number;
    minutes_per_unit: number;
    unit_assignments: {
        unit: string;
        cpt_code: string;
    }[];
}

type Options = {
    label: string;
    value: string;
};

const modifiersTypesForSelect: Options[] = Object.values(ModifierTypes).map(
    (item) => ({
        value: item,
        label: item,
    })
);

const CPTOptions = CPT_CODES_PSYCH.sort((a, b) => Number(a) - Number(b)).map(
    (code) => ({
        label: `${removeEnumUnderscore(code)} - ${CPT_CODES_DESCRIPTION[code]}`,
        value: code,
    })
);

const PlaceOfServiceOptions = Object.values(SessionPlaceOfServiceCode).map(
    (place) => ({
        value: place,
        label: place,
    })
);

export default function ChangeCodeFormPsych({
    notes,
    note,
    cptCode,
    onFormSubmit,
    showDetails = true,
}: ChangeCodeFormPsychProps) {
    const params = useParams();
    const noteToEditId = note?.note_id || (params.noteId as string);
    const clientId = note?.client?.client_id || (params.clientId as string);

    const isSmallScreen = window.matchMedia("(max-width: 600px)").matches;

    const cptCodeTemplates = (noteType: NoteTypes) => {
        switch (noteType) {
            case NoteTypes.TESTING_INTAKE_MINOR_INTAKE_PSYCH:
                return {
                    first: "96116",
                    additional: null,
                };
            case NoteTypes.ADMINISTRATION_PSYCH:
                return {
                    first: "96136",
                    additional: "96137",
                };
            case NoteTypes.SCORING_BY_PSYCH:
                return {
                    first: "96136",
                    additional: "96137",
                };
            case NoteTypes.ADMINISTRATION_TECH:
            case NoteTypes.SCORING_BY_TECH_PSYCH:
                return {
                    first: "96138",
                    additional: "96139",
                };
            case NoteTypes.INTEGRATING_PATIENT_DATA_PSYCH:
            case NoteTypes.INTERPRETATION_REPORT_WRITING_PSYCH:
                return {
                    first: "96132",
                    additional: "96133",
                };
            case NoteTypes.FEEDBACK_PSYCH:
                return {
                    first: "96132",
                    additional: "96133",
                };

            default:
                return {
                    first: null,
                    additional: null,
                };
        }
    };

    const { data, isLoading } = useFetchSessionNote(
        clientId,
        noteToEditId,
        Boolean(clientId) && Boolean(note?.note_id)
    );

    const changeCptCode = useChangeCptCode();

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const {
        control,
        handleSubmit,
        register,
        watch,
        setValue,
        formState: { errors },
    } = useForm<IChangeCodePsychPayload>({
        resolver: yupResolver(
            yup.object({
                modifier_codes: yup.array().of(yup.string().nullable()),
                pos_code: yup.string(),
                duration: yup
                    .string()
                    .required("Duration is required")
                    .matches(/^\d+$/, "Duration must be a valid number"),
                total_units: yup
                    .string()
                    .required("Total units is required")
                    .matches(/^\d+$/, "Total units must be a valid number"),
                minutes_per_unit: yup
                    .string()
                    .required("Minutes per unit is required")
                    .matches(
                        /^\d+$/,
                        "Minutes per unit can only be a positive integer"
                    ),
                unit_assignments: yup.array().of(
                    yup.object().shape({
                        unit: yup.string().required("Unit is required"),
                        cpt_code: yup.string().required("CPT code is required"),
                    })
                ),
            })
        ),
        mode: "onChange",
        defaultValues: {
            modifier_codes: data?.data.modifier_codes || [],
            unit_assignments:
                data?.data.note_metadata?.unit_calculation?.unit_assignments ||
                [],
        },
    });

    const noteToEditCptCode = cptCode || data?.data.cpt_code;

    const modifierCodesFromForm = watch("modifier_codes") || [];

    const canEditPlaceOfService =
        (!notes || (notes && notes.length === 0)) && data?.data.note_content;

    const unitCalculation = data?.data.note_metadata?.unit_calculation;

    const durationInMinutes = watch("duration") ? Number(watch("duration")) : 0;
    const minutesPerUnit = Number(watch("minutes_per_unit"));

    const debouncedDuration = useDebounce(durationInMinutes);
    const debouncedMinutesPerUnit = useDebounce(minutesPerUnit);

    const getTotalUnitsAmount =
        debouncedMinutesPerUnit > 0
            ? debouncedDuration / debouncedMinutesPerUnit
            : 0;

    const finalUnitsAmount =
        getTotalUnitsAmount >= 0.5
            ? Math.round(getTotalUnitsAmount)
            : Math.floor(getTotalUnitsAmount);

    const currentUnitAssignments = watch("unit_assignments");

    useEffect(() => {
        if (!unitCalculation?.unit_assignments || finalUnitsAmount <= 0) {
            return;
        }

        const currentAssignments =
            currentUnitAssignments || unitCalculation.unit_assignments;
        const currentLength = currentAssignments.length;

        if (finalUnitsAmount === currentLength) {
            return;
        }

        if (finalUnitsAmount > currentLength) {
            const newAssignments = [...currentAssignments];

            // Get the CPT code to use for additional units
            let repeatCptCode: string;

            if (currentLength === 1 && data?.data.type) {
                const template = cptCodeTemplates(data.data.type);

                if (template.additional) {
                    repeatCptCode = template.additional;
                } else if (template.first) {
                    repeatCptCode = template.first;
                } else {
                    repeatCptCode = currentAssignments[0].cpt_code;
                }
            } else {
                repeatCptCode =
                    currentAssignments.length > 1
                        ? currentAssignments[1].cpt_code
                        : currentAssignments[0].cpt_code;
            }

            const maxUnit = Math.max(
                ...(currentAssignments.map((a) => Number(a.unit)) || [])
            );

            for (let i = currentLength; i < finalUnitsAmount; i += 1) {
                newAssignments.push({
                    unit: String(maxUnit + (i - currentLength + 1)),
                    cpt_code: repeatCptCode,
                });
            }

            setValue("unit_assignments", newAssignments);
        } else if (finalUnitsAmount < currentLength) {
            const newAssignments = currentAssignments.slice(
                0,
                finalUnitsAmount
            );
            setValue("unit_assignments", newAssignments);
        }
    }, [
        finalUnitsAmount,
        unitCalculation?.unit_assignments,
        currentUnitAssignments,
        setValue,
        data?.data.type,
    ]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getAppointmentLocationToShow = (content: { [key: string]: any }) => {
        if (!content.appointment_location) return "--";

        if (AppointmentLocation.TELETHERAPY === content.appointment_location) {
            if (
                content.delivery_option &&
                content.delivery_option === "Other"
            ) {
                return content.other_delivery_option;
            }
            if (
                content.delivery_option &&
                content.delivery_option !== "Other"
            ) {
                return content.delivery_option;
            }
        }
        return removeEnumUnderscore(content.appointment_location);
    };

    const onSubmit = (payload: IChangeCodePsychPayload) => {
        interface Payload {
            [noteId: string]: {
                cpt_code: string;
                modifier_codes?: string[];
                pos_code?: string;
                confirm_code: boolean;
                unit_calculation?: {
                    duration: number;
                    total_units: number;
                    minutes_per_unit: number;
                    unit_assignments: {
                        unit: number;
                        cpt_code: string;
                    }[];
                };
            };
        }

        const modifierCode =
            (payload?.modifier_codes &&
                payload?.modifier_codes?.map((item) => item.split(" ")[0])) ||
            [];
        const changeCodePayload = {
            [noteToEditId]: {
                ...payload,
                cpt_code: noteToEditCptCode as string,
                confirm_code: false,
                modifier_codes:
                    modifierCode && modifierCode.length > 0
                        ? modifierCode
                        : undefined,
                pos_code: payload?.pos_code || undefined,
                unit_assignments: undefined,
                unit_calculation: payload.unit_assignments
                    ? {
                          duration: Number(payload.duration) || 0,
                          total_units: finalUnitsAmount || 0,
                          minutes_per_unit:
                              Number(payload?.minutes_per_unit) || 0,
                          unit_assignments: payload?.unit_assignments
                              ? payload.unit_assignments.map((unit) => ({
                                    cpt_code: unit.cpt_code,
                                    unit: Number(unit.unit),
                                }))
                              : [],
                      }
                    : undefined,
            },
        };

        // Multi-selected notes
        const changeCodesPayload: Payload =
            notes && notes.length !== 0
                ? notes?.reduce((item: Payload, singleNote: INote) => {
                      return {
                          ...item,
                          [singleNote.note_id]: {
                              cpt_code: singleNote.cpt_code || "",
                              confirm_code: false,
                              modifier_codes:
                                  modifierCode && modifierCode.length > 0
                                      ? modifierCode
                                      : undefined,
                              pos_code: payload?.pos_code || undefined,
                              unit_assignments: undefined,
                              unit_calculation: payload.unit_assignments
                                  ? {
                                        duration: Number(payload.duration) || 0,
                                        total_units: finalUnitsAmount || 0,
                                        minutes_per_unit:
                                            Number(payload?.minutes_per_unit) ||
                                            0,
                                        unit_assignments:
                                            payload?.unit_assignments
                                                ? payload.unit_assignments.map(
                                                      (unit) => ({
                                                          cpt_code:
                                                              unit.cpt_code,
                                                          unit: Number(
                                                              unit.unit
                                                          ),
                                                      })
                                                  )
                                                : [],
                                    }
                                  : undefined,
                          },
                      };
                  }, {})
                : {};

        changeCptCode.mutate(
            notes && notes?.length !== 0
                ? changeCodesPayload
                : changeCodePayload,
            {
                onSuccess: (response) => {
                    if (clientId && noteToEditId) {
                        queryClient.setQueryData<
                            IPaginatedApiResponse<IChangeCodeResponse>
                        >([clientId, `session-note`, noteToEditId], (prev) => {
                            const prevRequired =
                                prev as IPaginatedApiResponse<IChangeCodeResponse>;
                            return {
                                ...prevRequired,
                                note_status: response.status,
                            };
                        });
                    }
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `session-note`, noteToEditId],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["notes"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["get-claim", { note_id: noteToEditId }],
                    });
                    toast({
                        mode: response.claim_requires_attention
                            ? "warning"
                            : "success",
                        message: response.claim_requires_attention
                            ? "CPT code updated, but the claim has already progressed past the ready-to-submit stage — please review and resubmit it."
                            : response.message ||
                              "CPT code updated successfully",
                        duration: response.claim_requires_attention
                            ? 6000
                            : 2000,
                    });
                    onFormSubmit();
                },

                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not change code at this time",
                    });
                },
            }
        );
    };

    if (isLoading) {
        return <Loader wrapperClassName="h-[300px]" />;
    }

    return (
        <div className="w-full">
            {showDetails ? (
                <Card classNames="mb-[--spacing-header]">
                    <h2 className="text-base mb-[--spacing-header]">
                        NOTE DETAILS
                    </h2>
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-[--spacing-items]">
                        <div>
                            <p className="section-info col-span-2">NOTE TYPE</p>
                            <p className="section-value capitalize">
                                {makeStringFirstLetterCapital(
                                    removeEnumUnderscore(
                                        NoteLabels[data?.data.type as NoteTypes]
                                    )
                                )}
                            </p>
                        </div>

                        <div>
                            <p className="section-info">APPOINTMENT TYPE</p>
                            <p className="section-value capitalize">
                                {removeEnumUnderscore(
                                    data?.data.appointment_type || "--"
                                )}
                            </p>
                        </div>
                        <div>
                            <p className="section-info">PROVIDER NAME</p>
                            <p className="section-value">
                                {data?.data.provider
                                    ? `${data.data.provider.first_name} ${data.data.provider.last_name}`
                                    : "--"}
                            </p>
                        </div>
                        {unitCalculation ? (
                            <>
                                <div>
                                    <p className="section-info">Duration</p>
                                    <p className="section-value">
                                        {durationInMinutes || 0}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">
                                        Mins per unit
                                    </p>
                                    <p className="section-value">
                                        {watch("minutes_per_unit") || ""}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">
                                        Calculated units
                                    </p>
                                    <p className="section-value">
                                        {finalUnitsAmount}
                                    </p>
                                </div>
                            </>
                        ) : null}
                        {canEditPlaceOfService && (
                            <div>
                                <p className="section-info">
                                    Appointment location
                                </p>
                                <p className="section-value capitalize">
                                    {getAppointmentLocationToShow(
                                        data.data.note_content
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                </Card>
            ) : null}
            <form
                id="change-cpt-code"
                aria-label="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="fg flex justify-between three">
                    <Input
                        {...register("duration")}
                        label="Duration (In minutes)"
                        placeholder="Start time"
                        hasError={!!errors.duration}
                        errorText={errors.duration?.message}
                        defaultValue={unitCalculation?.duration}
                    />
                    <Input
                        {...register("minutes_per_unit")}
                        defaultValue={unitCalculation?.minutes_per_unit}
                        label="Mins per unit"
                        placeholder="Mins per unit"
                        hasError={!!errors.minutes_per_unit}
                        errorText={errors.minutes_per_unit?.message}
                    />
                    <Input
                        {...register("total_units")}
                        value={finalUnitsAmount}
                        label="Total units"
                        placeholder="Total units"
                        disabled
                    />
                </div>

                {currentUnitAssignments &&
                    currentUnitAssignments.map((assignment, i) => (
                        <div
                            key={`${i + 1} key`}
                            className="fg flex justify-between gap-[--spacing-items]"
                        >
                            <div className="basis-[30px]">
                                <Input
                                    {...register(`unit_assignments.${i}.unit`)}
                                    defaultValue={assignment.unit}
                                    label="Unit"
                                    placeholder="Unit"
                                    disabled
                                />
                            </div>
                            <div className="flex-1">
                                <Controller
                                    name={`unit_assignments.${i}.cpt_code`}
                                    control={control}
                                    defaultValue={
                                        CPTOptions.find(
                                            (code) =>
                                                code.value ===
                                                assignment.cpt_code
                                        )?.value
                                    }
                                    render={({ field }) => (
                                        <Select
                                            label="CPT Code"
                                            placeholder="CPT Code"
                                            options={CPTOptions}
                                            onChange={(val) =>
                                                field.onChange(
                                                    (val as Options).value
                                                )
                                            }
                                            formatOptionLabel={(
                                                selectData,
                                                meta
                                            ) => {
                                                return meta.context === "menu"
                                                    ? (selectData as Options)
                                                          .label
                                                    : truncateString(
                                                          (
                                                              selectData as Options
                                                          ).label,
                                                          isSmallScreen
                                                              ? 20
                                                              : 60
                                                      );
                                            }}
                                            isSearchable
                                            defaultValue={CPTOptions.find(
                                                (code) =>
                                                    code.value ===
                                                    assignment.cpt_code
                                            )}
                                            hasError={!!errors.cpt_code}
                                            errorText={errors.cpt_code?.message}
                                            isLongListInDialog
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    ))}
                <div className="fg">
                    <Controller
                        name="modifier_codes"
                        control={control}
                        defaultValue={data?.data.modifier_codes}
                        render={({ field }) => (
                            <Select
                                label="Modifier code"
                                placeholder="Modifier code"
                                options={modifiersTypesForSelect}
                                onChange={(val) => {
                                    field.onChange(
                                        (
                                            val as {
                                                label: string;
                                                value: string;
                                            }[]
                                        ).map((code) => code.value)
                                    );
                                }}
                                isSearchable
                                defaultValue={modifiersTypesForSelect?.filter(
                                    (code) =>
                                        data?.data.modifier_codes?.includes(
                                            code.value.split(" ")[0]
                                        )
                                )}
                                isMulti
                                multiHasValues={
                                    modifierCodesFromForm &&
                                    modifierCodesFromForm.length > 0
                                }
                                isLongListInDialog
                                closeMenuOnSelect
                                hideSelectedOptions={false}
                                hasError={!!errors.modifier_codes}
                                errorText={errors.modifier_codes?.message}
                            />
                        )}
                    />
                </div>
                {canEditPlaceOfService ? (
                    <Controller
                        name="pos_code"
                        control={control}
                        defaultValue={
                            PlaceOfServiceOptions.find(
                                (pos) => pos.value === data.data.pos_code
                            )?.value
                        }
                        render={({ field }) => (
                            <Select
                                label="POS code"
                                placeholder="POS code"
                                options={PlaceOfServiceOptions}
                                onChange={(val) =>
                                    field.onChange((val as Options).value)
                                }
                                defaultValue={PlaceOfServiceOptions.find(
                                    (pos) => pos.value === data.data.pos_code
                                )}
                                isSearchable
                                isLongListInDialog
                            />
                        )}
                    />
                ) : null}
            </form>
        </div>
    );
}
